import { FC, ReactNode, useRef, useEffect, useState, useMemo, RefObject } from 'react'
import clsx from 'clsx'
import Auth from 'store/auth'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import WebinarViewers from 'components/parts/webinar-viewers/webinar-viewers'
import ArticleDateViews from 'components/parts/article-date-views/article-date-views'
import AttachedItems from 'components/parts/attached-items/attached-items'
const NotAvaliable = dynamic(
  () => import('../../banners/banner-not-avaliable/banner-not-avaliable')
)
import VideoLectorsBlock from '../video-block/video-block-lectors'
const WebinarTemplate = dynamic(() => import('../webinar-template/webinar-template'))
import LikeShare from 'components/parts/like-share/like-share'
import Property from 'components/parts/property/property'
const Accordeon = dynamic(() => import('components/parts/accordeon/accordeon'))
import HtmlContainer from 'containers/html-container/html-container'
import Recommendations from 'containers/recommendations/recommendations'
import CrowdWebinarBanner from 'components/crowd-funding/crowd-webinar-banner/crowd-webinar-banner'
const BannerQuestionnaire = dynamic(
  () => import('components/banners/banner-questionnaire/banner-questionnaire')
)
const BannerQuestionnaireTeaser = dynamic(
  () => import('components/banners/banner-questionnaire-teaser/banner-questionnaire-teaser')
)

import { originMedic } from 'utils/analytics/other/other'
import { getHashFromUrl } from 'utils/getURLData'
import { defineWebinarType } from 'utils/defineWebinarType'
import { IMaterial, ILectorsData } from 'constants/types/webinar.types'
import { originSubscribe, originFWebinar } from 'utils/analytics/webinars/webinar_future'
import { STUDENT_TAG } from 'components/students/students-data'
import { CardsType } from 'constants/newsline'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import { WELCOME_MESSAGE } from 'constants/webinar-data'
import { Events } from 'constants/events'

import styles from './video-block-project.module.scss'

type VideoWithQuestionnaire = Omit<IMaterial, 'recomendation'>

interface IWebinarTranslation {
  dataset: VideoWithQuestionnaire & { recomendation?: any }
  viewers?: number
  lectorsData: ILectorsData
  isEnded?: boolean
  isMustBePaid?: boolean
  isTranslationPage?: boolean
  handleTimeCode?: (time: number) => void
  children?: ReactNode
  crowdRef?: RefObject<HTMLDivElement>
}

const VideoBlockInfo: FC<IWebinarTranslation> = ({
  dataset,
  viewers,
  lectorsData,
  isEnded,
  isMustBePaid,
  isTranslationPage,
  handleTimeCode,
  children,
  crowdRef,
}) => {
  const {
    ArticleTypeId,
    lecturName,
    description,
    descriptionShort,
    summary,
    name,
    Themes,
    startDate,
    filesList,
    htmlContent,
    id,
    points,
    activeNMOCodes,
    NMOTimeLength,
    checkupCount,
    privateKey,
    Specialities,
    linkTranslation,
    hasSponsor,
    tags,
    recomendation,
    isWithTimecodes,
    materialType,
  } = dataset

  const router = useRouter()
  const { isAuthorized } = Auth.useAuth()

  const [initState, setInitState] = useState(false)

  const realDescription = description || htmlContent || descriptionShort || ''
  const isSpecsInDescription = description && description.includes(WELCOME_MESSAGE)
  const eventType = defineWebinarType(id)
  const summaryRef = useRef<HTMLDivElement>(null)

  const webinarHasStudentTag = tags?.includes?.(STUDENT_TAG)

  const scrollToSummary = () => {
    if (!summaryRef) return

    summaryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  useEffect(() => {
    const handleScroll = () => {
      const initHash = getHashFromUrl(router.asPath)
      if (initHash === '#summary' && summary) {
        scrollToSummary()
        setInitState(true)
      }
    }
    window.addEventListener(Events.LOAD, handleScroll)
    return () => window.removeEventListener(Events.LOAD, handleScroll)
  }, [id])

  const currentBanner = useMemo(() => {
    let Banner = <></>

    if (recomendation) {
      if (recomendation.place === CardsType.FULL_MAIN)
        Banner = <BannerQuestionnaire questionnaire={recomendation} />
      else if (recomendation.place === CardsType.TEASER_MAIN)
        Banner = <BannerQuestionnaireTeaser {...recomendation} />
    }

    return Banner
  }, [recomendation])

  return (
    <div className={styles.info}>
      <div className={clsx(styles.headerBlock)}>
        <div
          className={clsx(styles.statsBorderedHeader, {
            [styles.inLineWrapper]: !isEnded,
          })}
        >
          {Themes && !isTranslationPage && (
            <div className={styles.stats}>
              {Themes.map((themeName, i) => {
                const isLast = Themes.length !== i + 1
                return (
                  <div key={themeName} className={styles.category}>
                    {themeName}
                    {isLast && <>,&nbsp;</>}
                  </div>
                )
              })}
            </div>
          )}
          <h1
            className={clsx(styles.title, styles.mainTitle, {
              [styles.titleTranslation]: isTranslationPage,
            })}
          >
            {name}
          </h1>

          {isEnded ? (
            <div>
              <div className={styles.likeBottomLine}>
                <LikeShare AData={{ origin: originShare.VIDEO }} />
                <div className={styles.articleViews}>
                  <ArticleDateViews date={startDate} cancelViewsCounting={isTranslationPage} />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.propetysWrapper}>
              {activeNMOCodes && points && (
                <div className={styles.nmoPointsWrapper}>
                  <Property text="НМО" modificator={styles.orangeProperty} />
                </div>
              )}
              {Boolean(viewers) && (
                <div className={styles.viewersWrapper}>
                  <WebinarViewers viewers={viewers} short />
                </div>
              )}
            </div>
          )}
        </div>

        {currentBanner}

        {children && <div className={clsx(styles.statsBordered, styles.noMargin)}>{children}</div>}

        {!isAuthorized && <NotAvaliable modificator="NMO" outer={true} />}
      </div>

      {summary && (
        <div className={styles.summaryWrapper} ref={summaryRef}>
          <Accordeon
            title="Основные тезисы вебинара"
            text={<HtmlContainer htmlContent={summary} />}
            modificator="fullWidth"
            initState={initState}
          />
        </div>
      )}

      <VideoLectorsBlock
        lectorsData={lectorsData}
        isEnded={isEnded}
        lecturName={lecturName}
        isTranslationPage={isTranslationPage}
      />

      <div>
        <WebinarTemplate
          id={id}
          linkTranslation={linkTranslation}
          points={points}
          NMOTimeLength={NMOTimeLength}
          isActiveNMOCodes={Boolean(activeNMOCodes)}
          checkupCount={checkupCount}
          privateKey={privateKey}
          Specialities={isSpecsInDescription ? undefined : Specialities}
          eventType={eventType}
          hasSponsor={hasSponsor}
          description={realDescription}
          titleModificator={isTranslationPage ? styles.titleTranslation : ''}
          modificator={styles.statsBordered}
          isEnded={isEnded}
          isWebinar={ArticleTypeId !== 2}
          handleTimeCode={handleTimeCode}
          isWithTimecodes={isWithTimecodes}
          materialType={materialType}
        />
        {isTranslationPage && (
          <CrowdWebinarBanner
            AData={{ origin: originMedic.WEBINAR_FUTURE }}
            isWide
            crowdRef={crowdRef}
          />
        )}
      </div>

      {isEnded && !isMustBePaid && filesList && Boolean(filesList.length) && (
        <div className={styles.statsBordered}>
          <AttachedItems items={filesList} />{' '}
        </div>
      )}
      {isTranslationPage && !webinarHasStudentTag && (
        <div className={styles.recomendation}>
          <h2
            className={clsx(styles.title, styles.recomendationTitle, {
              [styles.titleTranslation]: isTranslationPage,
            })}
          >
            Рекомендации 2442424
          </h2>
          <Recommendations
            AData={{
              originSub: originSubscribe.NOORIGIN,
              originClick: originFWebinar.NOORIGIN,
            }}
            eventWrapperModificator={styles.eventWrapperModificator}
          />
        </div>
      )}
    </div>
  )
}

export default VideoBlockInfo
